"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useAudioPeaks } from "@/hooks/useAudioPeaks";
import { EnvelopeFollower } from "@/lib/audio-envelope";

interface AudioWaveformProps {
  /** Unique identifier for generating consistent waveform pattern */
  soundId: string;
  /** Pre-computed waveform data (0-100 values). If not provided, will be derived from audioUrl or generated */
  waveformData?: number[];
  /** Audio source URL — when provided and no waveformData, real peaks are decoded client-side */
  audioUrl?: string | null;
  /** Current playback progress as percentage (0-100) */
  progress?: number;
  /** Whether the audio is currently playing */
  isPlaying?: boolean;
  /** Whether the audio is loading */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Number of bars to display (default: 24) */
  barCount?: number;
  /** Height of the waveform container */
  height?: string;
  /** Override color for played bars — any valid CSS color or var(). Falls back to --tls-brand. */
  accentColor?: string;
  /** Hide from the accessibility tree when a surrounding slider already names it. */
  decorative?: boolean;
}

const LOADING_DOTS = [
  { id: "loading-dot-one", delay: 0 },
  { id: "loading-dot-two", delay: 150 },
  { id: "loading-dot-three", delay: 300 },
] as const;

// Cache for generated waveform data
const waveformCache = new Map<string, number[]>();

/**
 * Generate deterministic waveform data from a sound ID
 * Creates a smooth, natural-looking audio waveform pattern
 */
const generateWaveformData = (id: string, barCount: number): number[] => {
  const cacheKey = `${id}-${barCount}`;

  if (waveformCache.has(cacheKey)) {
    return waveformCache.get(cacheKey)!;
  }

  // Handle invalid ID
  if (!id || typeof id !== "string") {
    return new Array(barCount).fill(50);
  }

  // Generate seed from ID
  const seed = id.split("").reduce((acc, char, idx) => acc + char.charCodeAt(0) * (idx + 1), 0);

  const data: number[] = [];

  for (let i = 0; i < barCount; i++) {
    // Create organic wave pattern using multiple sine waves
    const position = i / barCount;
    const wave1 = Math.sin(position * Math.PI * 2 + seed * 0.1) * 25;
    const wave2 = Math.sin(position * Math.PI * 4 + seed * 0.05) * 15;
    const wave3 = Math.sin(position * Math.PI * 6 + seed * 0.03) * 10;

    // Envelope to create natural fade at edges
    const envelope = Math.sin(position * Math.PI);

    // Combine waves with envelope and normalize to 20-95 range
    const value = 50 + (wave1 + wave2 + wave3) * envelope * 0.8;
    data.push(Math.max(20, Math.min(95, Math.round(value))));
  }

  waveformCache.set(cacheKey, data);
  return data;
};

/**
 * Audio waveform visualization component
 *
 * Features:
 * - Clean, minimal 1px tick ladder
 * - Progress indication via opacity
 * - Accent-led playback state without decorative effects
 * - Smooth transitions
 * - Reduced motion support
 */
export const AudioWaveform: React.FC<AudioWaveformProps> = ({
  soundId,
  waveformData,
  audioUrl,
  progress = 0,
  isPlaying = false,
  isLoading = false,
  className,
  barCount = 24,
  height = "48px",
  accentColor,
  decorative = false,
}) => {
  const hasStoredPeaks = Boolean(waveformData && waveformData.length > 0);
  // Decode real peaks from the audio file when no pre-computed waveform exists
  const { peaks: decodedPeaks, loading: peaksLoading } = useAudioPeaks(
    audioUrl,
    barCount,
    !hasStoredPeaks && Boolean(audioUrl)
  );

  const bars = useMemo(() => {
    const toBars = (values: number[]) =>
      values.map((value, index) => ({
        id: `${soundId || "anonymous"}-waveform-bar-${barCount}-${index}`,
        value,
      }));

    if (waveformData && waveformData.length > 0) {
      if (waveformData.length !== barCount) {
        const resampled: number[] = [];
        for (let i = 0; i < barCount; i++) {
          const srcIdx = Math.floor((i / barCount) * waveformData.length);
          resampled.push(waveformData[srcIdx] || 50);
        }
        return toBars(resampled);
      }
      return toBars(waveformData);
    }
    if (decodedPeaks && decodedPeaks.length > 0) {
      return toBars(decodedPeaks);
    }
    return toBars(generateWaveformData(soundId, barCount));
  }, [soundId, waveformData, decodedPeaks, barCount]);

  const showLoading = isLoading || (peaksLoading && !hasStoredPeaks);

  // §11.3 envelope follower + hard rate limiter, wired to the *real* signal
  // this component already has on hand: the current play-head bar's decoded
  // peak amplitude (real audio data via useAudioPeaks/waveformData, not a
  // synthetic curve). Without this, the leading-edge bar's highlight was a
  // raw opacity/colour swap the instant `progress` crossed a bar boundary,
  // exactly the flash pattern §11.3 exists to prevent. The follower smooths
  // that transition (120ms attack / 320ms release), the rate limiter holds
  // the level when direction changes exceed 3/s, and the 40%-per-step cap
  // bounds any single frame's luminance jump.
  const followerRef = useRef<EnvelopeFollower | null>(null);
  const [followedLevel, setFollowedLevel] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number>(0);
  const prefersReducedMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReducedMotionRef.current = mq.matches;
    const onChange = () => {
      prefersReducedMotionRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!followerRef.current) followerRef.current = new EnvelopeFollower();
  }, []);

  useEffect(() => {
    if (!isPlaying || bars.length === 0) {
      followerRef.current?.reset(0);
      // Deferred to a microtask so this reacts to the *external* isPlaying
      // transition rather than being a synchronous setState-during-effect
      // (matches hooks/useAudioLevel.ts's same guard).
      queueMicrotask(() => setFollowedLevel(0));
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const paintOnce = () => {
      const barIndex = Math.min(
        bars.length - 1,
        Math.max(0, Math.floor((progress / 100) * bars.length))
      );
      const target = Math.max(0, Math.min(1, (bars[barIndex]?.value ?? 0) / 100));
      const follower = followerRef.current;
      if (!follower) return;
      const followed = follower.push(target, 16.7);
      setFollowedLevel(followed);
    };

    if (prefersReducedMotionRef.current) {
      // State is still carried (the bar reflects the real peak at the
      // current play-head), it just stops repainting every frame.
      paintOnce();
      return;
    }

    const tick = (ts: number) => {
      if (cancelled) return;
      const dt = lastTsRef.current ? ts - lastTsRef.current : 16.7;
      lastTsRef.current = ts;

      const barIndex = Math.min(
        bars.length - 1,
        Math.max(0, Math.floor((progress / 100) * bars.length))
      );
      const target = Math.max(0, Math.min(1, (bars[barIndex]?.value ?? 0) / 100));
      const follower = followerRef.current;
      if (follower) {
        setFollowedLevel(follower.push(target, dt));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- progress/bars drive the RAF loop's own reads, not effect re-subscription; re-running per progress tick would restart the follower every frame and defeat the envelope
  }, [isPlaying, bars.length]);

  return (
    <div
      className={cn(
        "relative flex w-full items-end gap-1",
        "overflow-hidden rounded-none bg-transparent",
        className
      )}
      style={{ height }}
      // eslint-disable-next-line react-doctor/prefer-tag-over-role -- div waveform built from child bars; <img> is for bitmap/vector images, not composite visualizations
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : (isPlaying ? "Audio waveform - playing" : "Audio waveform")}
    >
      {/* Bars */}
      {bars.map(({ id, value }, index) => {
        const barProgress = (index / bars.length) * 100;
        const isActive = progress > barProgress;
        const isCurrentBar =
          progress > barProgress && progress <= ((index + 1) / bars.length) * 100;

        // The leading-edge bar's highlight strength is the §11.3
        // envelope-followed + rate-limited level (followedLevel), not a raw
        // on/off swap at the bar boundary; see the effect above. 24% is the
        // resting lightening so the bar never goes fully to base color on a
        // near-silent frame, matching the previous binary swap's brightness
        // floor.
        const highlightMix = isCurrentBar && isPlaying ? 24 + followedLevel * 55 : 0;

        return (
          <div
            key={id}
            className={cn(
              "min-w-px flex-1 rounded-none transition-[background-color,opacity] duration-[var(--motion-duration-fast)]",
              "origin-bottom",
              !accentColor && !isActive && "bg-[var(--tls-border-strong)]",
              !accentColor && isActive && !isCurrentBar && "bg-[var(--tls-brand)]"
            )}
            style={{
              height: `${value}%`,
              ...(isCurrentBar && isPlaying
                ? {
                    backgroundColor: `color-mix(in srgb, ${accentColor ?? "var(--tls-brand)"} ${100 - highlightMix}%, white ${highlightMix}%)`,
                  }
                : accentColor
                  ? {
                      backgroundColor: isActive ? accentColor : "var(--tls-border-strong)",
                    }
                  : {}),
            }}
          />
        );
      })}

      {/* Loading overlay */}
      {showLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--tls-surface)_94%,transparent)]">
          <div className="flex gap-1">
            {LOADING_DOTS.map(({ id, delay }) => (
              <div
                key={id}
                className="size-1.5 rounded-[var(--radius-pill)] bg-[var(--tls-brand)]"
                style={{ animationDelay: `${delay}ms` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact variant for smaller spaces
 */
export const AudioWaveformCompact: React.FC<
  Omit<AudioWaveformProps, "barCount" | "height">
> = props => {
  return <AudioWaveform {...props} barCount={16} height="32px" />;
};
